
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Mail, CheckCircle } from 'lucide-react';

interface PremiumForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentSlug: string;
}

const PREMIUM_RESET_SLUG_STORAGE_KEY = 'premium-reset-slug';
const PREMIUM_FALLBACK_BASE_URL = 'https://maturity-compass.gapstars.dev';

const getPremiumResetBaseUrl = () => {
  const currentOrigin = window.location.origin.replace(/\/+$/, '');
  const isCustomDomain = /gapstars\.(dev|net)$/.test(window.location.hostname);

  return isCustomDomain ? currentOrigin : PREMIUM_FALLBACK_BASE_URL;
};

export default function PremiumForgotPasswordDialog({ open, onOpenChange, assessmentSlug }: PremiumForgotPasswordDialogProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    // Check if email exists in organization_members
    const { data: exists, error: checkError } = await supabase.rpc('check_premium_email_exists', {
      email_address: normalizedEmail,
    });

    if (checkError) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }

    if (!exists) {
      setError('This email does not have an account.');
      setLoading(false);
      return;
    }

    if (assessmentSlug) {
      try {
        localStorage.setItem(PREMIUM_RESET_SLUG_STORAGE_KEY, assessmentSlug);
      } catch {
        // ignore storage errors
      }
    }

    const slugQuery = assessmentSlug ? `?slug=${encodeURIComponent(assessmentSlug)}` : '';
    const redirectTo = `${getPremiumResetBaseUrl()}/premium-reset-password${slugQuery}`;

    const { error: resetError } = await supabase.functions.invoke('send-password-reset', {
      body: {
        email: normalizedEmail,
        redirectTo,
        flavor: 'premium',
      },
    });

    if (resetError) {
      setError(resetError.message || 'Failed to send reset email. Please try again.');
    } else {
      setSuccess(true);
    }
    setLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Reset Password
          </DialogTitle>
          <DialogDescription>
            Enter your email address and we'll send you a link to reset your password.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Password reset email sent! Check your inbox and follow the instructions to reset your password.
              </AlertDescription>
            </Alert>
            <Button onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="premium-reset-email">Email</Label>
              <Input
                id="premium-reset-email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Sending...' : 'Send Reset Email'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
